import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Info } from 'lucide-react';
import { Recipe, Service } from '@/shared/model';
import { ResponsiveModal } from '@/shared/ui/responsive-modal';
import { Button } from '@/shared/ui/base';

interface RecipePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipe: Recipe | null;
  servicesDB: Service[];
  onApply: (recipe: Recipe) => void;
}

export const RecipePreviewModal: FC<RecipePreviewModalProps> = ({ isOpen, onClose, recipe, servicesDB, onApply }) => {
  const { t } = useTranslation();

  if (!recipe) return null;

  return (
    <ResponsiveModal
        open={isOpen}
        onOpenChange={(open) => !open && onClose()}
        title={recipe.nameKey ? t(recipe.nameKey, recipe.name) : t(`db.${recipe.name}`, recipe.name)}
        className="max-w-lg"
        stickyHeader
    >
        <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-2xl flex gap-3 border border-blue-100 dark:border-blue-900/30">
                <Info className="text-blue-600 shrink-0" size={20}/>
                <p className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed font-medium">
                    {t('recipes.preview.description', 'Данный шаблон содержит предварительно настроенный список услуг. После добавления вы сможете отредактировать количество каждой услуги отдельно.')}
                </p>
            </div>

            <div className="space-y-3">
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">{t('recipes.preview.layers_title', 'СОСТАВ ПАКЕТА')}</h4>
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
                    {recipe.layers.map((layer, idx) => {
                        const service = servicesDB.find(s => s.id === layer.serviceId);
                        if (!service) return null;
                        
                        return (
                            <div key={idx} className="p-3 flex items-center justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-sm text-slate-700 dark:text-slate-200 truncate">
                                        {service.nameKey ? t(service.nameKey, service.name) : service.name}
                                    </div>
                                    <div className="text-[10px] text-slate-400 uppercase font-bold">{service.unit}</div>
                                </div>
                                {layer.coefficient !== 1 && (
                                    <div className="bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-lg text-[10px] font-bold text-slate-500">
                                        {/* eslint-disable-next-line i18next/no-literal-string */}
                                        <span className="font-mono tabular-nums">x{layer.coefficient}</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="flex flex-col gap-2 pt-2">
                <Button 
                    variant="primary" 
                    className="h-14 bg-purple-600 hover:bg-purple-700 shadow-purple-200 dark:shadow-none font-bold text-lg"
                    onClick={() => { onApply(recipe); onClose(); }}
                >
                    <Check size={24}/> {t('recipes.preview.apply_btn', 'ДОБАВИТЬ В КОМНАТУ')}
                </Button>
                <Button variant="ghost" onClick={onClose} className="text-slate-400">{t('common.cancel', 'Отмена')}</Button>
            </div>
        </div>
    </ResponsiveModal>
  );
};
